import { adresseDeFacturation } from '@imp/shared/controle-emission';
import type { ClientRecherchable } from '@imp/shared/recherche-client';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';

// Organisation unique (tenant) — défini au seed. RLS exige organization_id à l'insert.
export const ORG_ID = '00000000-0000-0000-0000-000000000001';

export interface Contact {
  id: string;
  kind: string;
  type_personne: string;
  civilite: string | null;
  nom: string | null;
  prenom: string | null;
  raison_sociale: string | null;
  siret: string | null;
  tva_intracom: string | null;
  naf: string | null;
  email: string | null;
  telephone: string | null;
  telephone_mobile: string | null;
  notes: string | null;
  /** Numéro de compte client en comptabilité (C00012), attribué une fois pour toutes. */
  code_comptable: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

// Suivi de l'avis Google client (demande après chantier) — stocké dans metadata.avis_google.
export type AvisGoogle = 'non_demande' | 'demande' | 'laisse';

export const AVIS_GOOGLE_LABELS: Record<AvisGoogle, string> = {
  non_demande: 'Non demandé',
  demande: 'Demandé',
  laisse: 'Avis laissé',
};

export function avisGoogle(c: Pick<Contact, 'metadata'>): AvisGoogle {
  const v = c.metadata?.avis_google;
  return v === 'demande' || v === 'laisse' ? v : 'non_demande';
}

export interface ContactInput {
  kind: string;
  code_comptable?: string;
  type_personne: string;
  civilite: string;
  nom: string;
  prenom: string;
  raison_sociale: string;
  siret: string;
  tva_intracom: string;
  naf: string;
  email: string | null;
  telephone: string;
  telephone_mobile: string;
  notes: string;
  // Fusionné côté formulaire avec l'existant (ne pas écraser les autres clés metadata).
  metadata?: Record<string, unknown>;
}

export async function listContacts(): Promise<Contact[]> {
  // Lu par tranches : PostgREST plafonne une réponse à 1 000 lignes et NE
  // SIGNALE PAS la troncature. Jusqu'au 06/10/2026, la liste s'arrêtait au
  // milieu des « A » : des clients manquaient, et les devis de ceux-là
  // s'affichaient sans nom — c'est cette même lecture qui fournit les noms au
  // reste de l'application.
  //
  // Le second tri, sur l'identifiant, n'est pas décoratif : deux fiches créées
  // dans la même seconde (un import) n'ont pas d'ordre fixe, et d'une tranche
  // à l'autre l'une pourrait être lue deux fois et l'autre jamais.
  return lireTout<Contact>((de, a) =>
    supabase
      .from('contacts')
      .select('*')
      .order('created_at', { ascending: false })
      .order('id')
      .range(de, a)
      .overrideTypes<Contact[], { merge: false }>(),
  );
}

export async function getContact(id: string): Promise<Contact> {
  const { data, error } = await supabase.from('contacts').select('*').eq('id', id).single();
  if (error) throw new Error(error.message);
  return data as Contact;
}

// Un code vide part en null, jamais en chaîne vide : l'index d'unicité ne
// tolérerait qu'un seul client « sans code ».
const nettoyerCode = (
  input: ContactInput,
): Omit<ContactInput, 'code_comptable'> & { code_comptable: string | null } => {
  const code = input.code_comptable?.trim().toUpperCase() ?? '';
  return { ...input, code_comptable: code === '' ? null : code };
};

export async function createContact(input: ContactInput): Promise<string> {
  const { data, error } = await supabase
    .from('contacts')
    .insert({ ...nettoyerCode(input), organization_id: ORG_ID })
    .select('id')
    .single()
    .overrideTypes<{ id: string }, { merge: false }>();
  if (error) throw new Error(error.message);
  return data.id;
}

export async function updateContact(id: string, input: ContactInput): Promise<void> {
  const { error } = await supabase.from('contacts').update(nettoyerCode(input)).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteContact(id: string): Promise<void> {
  const { error } = await supabase.from('contacts').delete().eq('id', id);
  if (error) {
    // FK on delete restrict : client référencé par un devis ou un chantier.
    if (error.code === '23503' || /foreign key/i.test(error.message)) {
      throw new Error('Ce client est lié à des devis ou chantiers : suppression impossible.');
    }
    throw new Error(error.message);
  }
}

// Nom affichable d'un contact (particulier : civilité + prénom + nom ; pro : raison sociale).
export function contactDisplayName(c: Pick<Contact, 'type_personne' | 'nom' | 'prenom' | 'raison_sociale'>): string {
  if (c.type_personne === 'pro') return c.raison_sociale ?? '(sans nom)';
  const full = [c.prenom, c.nom].filter(Boolean).join(' ').trim();
  return full || '(sans nom)';
}

// Le contact tel que la recherche partagée l'attend : nom affiché, téléphones,
// email, code comptable — tout ce sur quoi on peut vouloir le retrouver.
export function versRecherchable(c: Contact): ClientRecherchable {
  return {
    id: c.id,
    nom: contactDisplayName(c),
    email: c.email,
    telephone: c.telephone,
    telephoneMobile: c.telephone_mobile,
    codeComptable: c.code_comptable,
  };
}

// ----- Adresse postale ------------------------------------------------------
//
// La table `adresses` accepte plusieurs adresses par client (facturation,
// chantier, siège). Le CRM n'en saisit qu'UNE, celle du courrier et de la
// facture : c'est elle que le PDF de devis et la facture vont chercher en
// premier, et une facture sans adresse client n'est pas en règle.

export interface Adresse {
  id: string;
  usage: string;
  ligne1: string;
  ligne2: string | null;
  code_postal: string;
  ville: string;
  pays: string;
  est_default: boolean;
  created_at: string;
}

export interface AdresseInput {
  ligne1: string;
  ligne2: string | null;
  code_postal: string;
  ville: string;
}

const CHAMPS_ADRESSE = 'id,usage,ligne1,ligne2,code_postal,ville,pays,est_default,created_at';

/**
 * L'adresse que la fiche client montre et modifie : EXACTEMENT celle que
 * l'émission figera sur la facture. Une règle différente ici laissait corriger
 * une adresse pendant que la facture en figeait une autre.
 */
function principale(l: Adresse[]): Adresse | null {
  return adresseDeFacturation(l);
}

/**
 * Toutes les adresses d'un client — et l'erreur remonte. Le récapitulatif
 * avant émission s'en sert : une lecture ratée qui rendrait « pas d'adresse »
 * bloquerait l'émission pour une raison fausse.
 */
export function listAdressesClient(contactId: string): Promise<Adresse[]> {
  return listAdresses(contactId);
}

async function listAdresses(contactId: string): Promise<Adresse[]> {
  const { data, error } = await supabase
    .from('adresses')
    .select(CHAMPS_ADRESSE)
    .eq('contact_id', contactId)
    .is('deleted_at', null)
    .overrideTypes<Adresse[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

/**
 * L'adresse du client, ou null s'il n'en a pas.
 * Une lecture qui échoue ne doit pas empêcher d'ouvrir la fiche : on rend null
 * et le formulaire s'affiche vide plutôt que de rester bloqué.
 */
export async function getAdressePrincipale(contactId: string): Promise<Adresse | null> {
  if (contactId === '') return null;
  try {
    return principale(await listAdresses(contactId));
  } catch {
    return null;
  }
}

/**
 * Enregistre l'adresse du client : créée, mise à jour, ou retirée si le
 * formulaire est revenu vide. Ici, en revanche, une erreur remonte — on ne
 * laisse pas croire qu'une adresse est enregistrée alors qu'elle ne l'est pas.
 */
export async function enregistrerAdressePrincipale(
  contactId: string,
  input: AdresseInput | null,
): Promise<void> {
  const existante = principale(await listAdresses(contactId));

  if (input === null) {
    if (existante === null) return;
    const { error } = await supabase
      .from('adresses')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', existante.id);
    if (error) throw new Error(error.message);
    return;
  }

  if (existante === null) {
    const { error } = await supabase.from('adresses').insert({
      ...input,
      contact_id: contactId,
      organization_id: ORG_ID,
      usage: 'facturation',
      est_default: true,
    });
    if (error) throw new Error(error.message);
    return;
  }

  const { error } = await supabase.from('adresses').update(input).eq('id', existante.id);
  if (error) throw new Error(error.message);
}
